import { useState } from 'react'
import { format, isToday, isYesterday } from 'date-fns'

import { Modal } from '@/ui/Modal'
import { Button } from '@/ui/Button'
import { TextArea } from '@/ui/TextArea'
import { useModal } from '@/components/Modal'
import { useNotification } from '@/ui/Notification'
import { LogUnfinishedMetadata } from '@/types/logs'
import { useEditFlagRecord } from '../../api/useEditFlagRecord'
import { useFlagRecordReopen } from '../../api/useFlagRecordReopen'

import EditCommentIcon from '../../assets/edit-comment.svg'
import FlagIcon from '@/modules/CombinedLogs/assets/flag.svg'
import styles from './EditFlagModal.module.scss'

export type EditFlagModalProps = {
    logId: string
    metadata: LogUnfinishedMetadata
}

const MAX_COMMENT_LENGTH = 200

const formatFlagDate = (date: string): string => {
    if (isToday(date)) return `${format(date, 'HH:mm')} today`
    if (isYesterday(date)) return `${format(date, 'HH:mm')} yesterday`
    return format(date, 'HH:mm dd/MM/yyyy')
}

export const EditFlagModal = () => {
    const { closeModal, props } = useModal<EditFlagModalProps>()
    const [isEditing, setIsEditing] = useState(false)
    const [isConfirmingUnflag, setIsConfirmingUnflag] = useState(false)
    const [comment, setComment] = useState(props?.metadata.comment ?? '')
    const { mutate, isPending: editPending } = useEditFlagRecord()
    const { mutate: reopen, isPending: reopenPending } = useFlagRecordReopen()
    const { showNotification } = useNotification()

    const isPending = editPending || reopenPending

    if (!props) return null

    const { metadata } = props

    const handleUnflag = () => {
        mutate(
            { flagMetadataId: metadata.id, comment: metadata.comment, isFlagged: false },
            {
                onSuccess: () => {
                    closeModal()
                    showNotification({
                        type: 'submit',
                        size: 'small',
                        message: 'Flag removed',
                        icon: (
                            <span style={{ color: 'var(--icon-highlight-primary)' }}>
                                <FlagIcon />
                            </span>
                        ),
                    })
                },
                onError: () => closeModal(),
            }
        )
    }

    const handleSaveEdit = () => {
        if (isDone) {
            reopen({ logId: props.logId, comment }, { onError: () => closeModal() })
        } else {
            mutate(
                { flagMetadataId: metadata.id, comment },
                {
                    onSuccess: () => {
                        closeModal()
                        showNotification({
                            className: styles.notification,
                            message: (
                                <div>
                                    <EditCommentIcon />
                                    <p>Comment edited</p>
                                </div>
                            ),
                        })
                    },
                    onError: () => closeModal(),
                }
            )
        }
    }

    const isDone = metadata.flagState === 'Done'

    if (isConfirmingUnflag) {
        return (
            <Modal className={styles.modal} onClose={closeModal} hideCloseButton>
                <div className={styles.information}>
                    <h1 className={styles.title}>Remove flag?</h1>
                    <p className={styles.description}>
                        This item will be marked as ready to code and will not be reviewed by a Performance Manager. Are
                        you sure you want to remove this flag?
                    </p>
                </div>
                <div className={styles.footer}>
                    <Button
                        rounded
                        variant="tertiary"
                        onClick={() => setIsConfirmingUnflag(false)}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button rounded variant="secondary" onClick={handleUnflag} disabled={isPending}>
                        Remove flag
                    </Button>
                </div>
            </Modal>
        )
    }

    return (
        <Modal className={styles.modal} onClose={closeModal} hideCloseButton>
            <div className={styles.information}>
                <h1 className={styles.title}>{isDone ? 'Review Flag' : 'Edit Flag'}</h1>
                <p className={styles.description}>
                    {isDone
                        ? `This flag has been marked as done by ${metadata.author}. Unflag it or reopen below if it is not resolved yet.`
                        : 'Edit the flag or unflag it if resolved'}
                </p>
            </div>
            <div className={styles.body}>
                <p className={styles.originalLabel}>
                    Original Comment -{' '}
                    <strong>
                        {metadata.author} {formatFlagDate(metadata.createdAt)}
                    </strong>
                </p>
                <TextArea
                    value={comment}
                    counter
                    maxLength={MAX_COMMENT_LENGTH}
                    disabled={!isEditing}
                    className={styles.textarea}
                    onChange={(e) => setComment(e.target.value)}
                />
            </div>
            <div className={styles.footer}>
                <Button rounded variant="tertiary" onClick={closeModal} disabled={isPending}>
                    Cancel
                </Button>
                {isEditing ? (
                    <Button rounded variant="secondary" onClick={handleSaveEdit} disabled={!comment || isPending}>
                        {isDone ? 'Save & Reopen Flag' : 'Save edit'}
                    </Button>
                ) : (
                    <>
                        <Button rounded variant="tertiary" onClick={() => setIsEditing(true)} disabled={isPending}>
                            {isDone ? 'Reopen flag' : 'Edit Comment'}
                        </Button>
                        <Button
                            rounded
                            variant="secondary"
                            onClick={() => setIsConfirmingUnflag(true)}
                            disabled={isPending}
                        >
                            Unflag Record
                        </Button>
                    </>
                )}
            </div>
        </Modal>
    )
}
